// src/pages/fleet/mapKit.ts — one Leaflet setup for every fleet map.
// • gentle zoom/pan (smaller wheel steps, softer inertia, slower animations)
// • Map / Satellite switch (OSM streets ↔ Esri imagery + place labels)
// • Google-style movement pad: pan arrows, zoom in/out, recentre
// Esri World Imagery is free to use with attribution; Google's own tiles are not.

// ── The street basemap ───────────────────────────────────────────────────────
//
// Vector tiles drawn by MapLibre, served by OpenFreeMap. We moved off OSM's own
// raster tiles because their policy forbids heavy use without permission and a
// fleet tracked across a state is plainly that: the data is free, the OSMF's
// hardware is not. OpenFreeMap serves the same OpenStreetMap data with no key,
// no quota and commercial use allowed.
//
// Styles are env-overridable because the way off OpenFreeMap, if it ever stops
// being free or reliable, is to point these at our own server. That is not
// hypothetical: tiles/ holds a PMTiles archive built with planetiler, and it
// carries the identical OpenMapTiles schema at the identical z0–14, so a
// self-hosted style needs no change to anything here.
//
// positron / dark rather than the colourful "liberty": this map is a backdrop
// for green trails, red deviations and coloured truck pins, and a basemap that
// competes with them makes the data harder to read, not the map prettier.
const OFM = 'https://tiles.openfreemap.org/styles';
const STYLE_LIGHT = process.env.REACT_APP_BASEMAP_STYLE_LIGHT || `${OFM}/positron`;
const STYLE_DARK = process.env.REACT_APP_BASEMAP_STYLE_DARK || `${OFM}/dark`;

// Raster OSM, kept only for the case where the MapLibre script does not load at
// all — a blocked CDN, an old browser without WebGL. Not a toggle: if MapLibre
// is there, it is used.
const STREET = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const IMAGERY = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const LABELS = 'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}';

// OpenFreeMap asks for credit, and the OpenMapTiles schema licence requires it.
const VECTOR_CREDIT = '© OpenFreeMap © OpenMapTiles © OpenStreetMap';

/** Dark mode is a real dark basemap now, not the old invert() filter over a
 *  light one — which turned parks pink and roads grey-on-grey.
 *
 *  data-theme sits on a wrapper div inside the app shell, not on <html>, so it
 *  is found from the map element upwards. Reading it off documentElement looked
 *  right and silently always answered "light". */
const themeHost = (el: HTMLElement): HTMLElement =>
  (el.closest('[data-theme]') as HTMLElement) || document.documentElement;

const styleFor = (el: HTMLElement): string =>
  themeHost(el).getAttribute('data-theme') === 'dark' ? STYLE_DARK : STYLE_LIGHT;

import type * as Leaflet from 'leaflet';

// Zoom limits. Esri World Imagery has real photos only up to z18 around the
// Ramagundam plant and rural India (z19 in big cities); beyond that it serves a
// grey "Map data not yet available" tile. So we fetch at most z18 imagery.
// Upscaling it 4× (to z20) turned the photo into a blur, so Satellite stops at
// z19 (one 2× step) while the street map still goes to z20.
const SAT_NATIVE_MAX = 18;
const SAT_MAX_ZOOM = 19;
const STREET_NATIVE_MAX = 19;
const MAX_ZOOM = 20;

/** Leaflet map with the fleet-specific "recentre target" setter. */
export interface FleetMap extends Leaflet.Map {
  fleetSetHome(bounds: Leaflet.LatLngBoundsExpression): void;
}

interface Home {
  center: Leaflet.LatLngExpression;
  zoom: number;
  bounds: Leaflet.LatLngBoundsExpression | null;
}

const ANIM: Leaflet.PanOptions = { animate: true, duration: 0.5, easeLinearity: 0.24 };

const svg = (d: string): string => `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;
const ICON = {
  up: svg('<path d="m6 15 6-6 6 6"/>'), down: svg('<path d="m6 9 6 6 6-6"/>'),
  left: svg('<path d="m15 6-6 6 6 6"/>'), right: svg('<path d="m9 6 6 6-6 6"/>'),
  plus: svg('<path d="M12 5v14M5 12h14"/>'), minus: svg('<path d="M5 12h14"/>'),
  home: svg('<path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/>'),
};

// ── RFCL plant (every trip starts here) ──────────────────────────────────────
// Shown with a little factory badge instead of a plain pin.
export const PLANT = {
  lat: 18.77850,
  lng: 79.50310,
  name: 'RFCL Bagging Plant',
  address: 'Bagging Building, Fertilizer Corporation Of India, Godavarikhani, Ramagundam, Telangana',
};

/** True when a point is the plant (within ~1.5 km). */
export function isPlant(lat: number, lng: number): boolean {
  return Math.abs(lat - PLANT.lat) < 0.015 && Math.abs(lng - PLANT.lng) < 0.015;
}

// A clean factory glyph — solid shape so it stays crisp at marker size.
const PLANT_SVG = `<svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">
  <path fill="#1DB954" d="M2.9 20.4V10.3c0-.6.7-1 1.2-.6l4.3 2.9V10.3c0-.6.7-1 1.2-.6l4.3 2.9V10.3c0-.6.7-1 1.2-.6l4.8 3.2c.3.2.4.4.4.7v6.8c0 .4-.3.7-.7.7H3.6a.7.7 0 0 1-.7-.7Z"/>
  <rect x="16.4" y="2.6" width="2.6" height="7.2" rx="1.1" fill="#1DB954"/>
  <rect x="6.2" y="15.4" width="2.4" height="5" rx=".6" fill="#fff"/>
  <rect x="11" y="15.4" width="2.4" height="5" rx=".6" fill="#fff"/>
  <rect x="15.8" y="15.4" width="2.4" height="5" rx=".6" fill="#fff"/>
</svg>`;

/** Plant marker — a small white badge with a factory glyph. */
export function plantMarker(at?: { lat: number; lng: number }): Leaflet.Marker {
  const L = window.L;
  return L.marker([at?.lat ?? PLANT.lat, at?.lng ?? PLANT.lng], {
    icon: L.divIcon({
      className: 'fx-plant',
      html: `<div class="fx-plant-b">${PLANT_SVG}</div><i class="fx-plant-tip"></i>`,
      iconSize: [36, 44],
      iconAnchor: [18, 44],
      popupAnchor: [0, -40],
    }),
    zIndexOffset: 500,
  }).bindPopup(`<b>${PLANT.name}</b><br/>${PLANT.address}`);
}

export const PLANT_CSS = `
  .fx-plant { background: none !important; border: none !important; }
  .fx-plant-b { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
    background: #fff; border: 1.5px solid rgba(29,185,84,.35); box-shadow: 0 2px 8px rgba(0,0,0,.22); }
  .fx-plant-tip { display: block; width: 8px; height: 8px; margin: -4px auto 0; background: #fff; transform: rotate(45deg);
    border-right: 1.5px solid rgba(29,185,84,.35); border-bottom: 1.5px solid rgba(29,185,84,.35); }
`;

/** Street-level zoom used when the user picks a vehicle. */
export const VEHICLE_ZOOM = 16;

/** Fly to a point at street level in one move (never zooms out if already closer). */
export function zoomTo(map: Leaflet.Map, at: Leaflet.LatLngExpression): void {
  const z = Math.min(Math.max(map.getZoom(), VEHICLE_ZOOM), map.getMaxZoom());
  map.flyTo(at, z, { duration: 1.1, easeLinearity: 0.25 });
}

export function createFleetMap(el: HTMLElement, center: Leaflet.LatLngExpression, zoom: number): FleetMap {
  const L = window.L;
  const map = L.map(el, {
    maxZoom: MAX_ZOOM,
    zoomControl: false,          // replaced by the movement pad below
    attributionControl: true,
    zoomSnap: 0.5,               // half-level steps instead of full jumps
    zoomDelta: 0.5,
    wheelPxPerZoomLevel: 130,    // default 60 → wheel/trackpad zooms gently
    wheelDebounceTime: 55,
    inertia: true,
    inertiaDeceleration: 2600,
    easeLinearity: 0.2,
  }).setView(center, zoom) as FleetMap;
  map.attributionControl.setPrefix(false);

  // The renderer is a CDN global like Leaflet itself, so a blocked script or a
  // browser without WebGL must not take the map down with it — it falls back to
  // raster instead.
  // Both, not either. The plugin and the engine are separate scripts, and the
  // plugin loading while the engine fails leaves L.maplibreGL defined but
  // unusable — which took the whole map down rather than falling back.
  const vector = typeof L.maplibreGL === 'function' && !!window.maplibregl;
  const makeStreet = (): Leaflet.Layer => (vector
    ? L.maplibreGL({
      style: styleFor(el),
      // MapLibre otherwise pushes the style's own credit into Leaflet's
      // attribution bar and never takes it back out, so switching to Satellite
      // left the map crediting a tile provider it was no longer showing. The
      // plugin ignores Leaflet's own `attribution` option, so with MapLibre's
      // control off the credit is added and removed by hand in showBase().
      attributionControl: false,
      // The MapLibre canvas is a backdrop; Leaflet owns every interaction on
      // this map, and letting MapLibre handle its own would fight the pan pad
      // and the marker clicks drawn above it.
      interactive: false,
    })
    : L.tileLayer(STREET, { maxZoom: MAX_ZOOM, maxNativeZoom: STREET_NATIVE_MAX, attribution: '© OpenStreetMap' }));

  let street = makeStreet();
  // The container is tagged so the dark-mode invert() in FleetAppShell skips a
  // basemap that already has a dark theme of its own.
  el.classList.toggle('fx-vector', vector);

  const satellite = L.layerGroup([
    L.tileLayer(IMAGERY, { maxZoom: MAX_ZOOM, maxNativeZoom: SAT_NATIVE_MAX, attribution: 'Imagery © Esri' }),
    L.tileLayer(LABELS, { maxZoom: MAX_ZOOM, maxNativeZoom: SAT_NATIVE_MAX }),
  ]);
  // Satellite caps the zoom (see SAT_MAX_ZOOM); a deeper street view eases back out.
  satellite.on('add', () => {
    map.setMaxZoom(SAT_MAX_ZOOM);
    if (map.getZoom() > SAT_MAX_ZOOM) map.setZoom(SAT_MAX_ZOOM, { animate: true });
  });
  satellite.on('remove', () => map.setMaxZoom(MAX_ZOOM));

  let base: 'street' | 'satellite' = 'street';
  const showBase = (which: 'street' | 'satellite'): void => {
    base = which;
    [street, satellite].forEach((l) => map.hasLayer(l) && map.removeLayer(l));
    (which === 'street' ? street : satellite).addTo(map);
    // OpenFreeMap asks for credit and the OpenMapTiles licence requires it, so
    // it is shown while its tiles are, and only while.
    if (vector) {
      if (which === 'street') map.attributionControl.addAttribution(VECTOR_CREDIT);
      else map.attributionControl.removeAttribution(VECTOR_CREDIT);
    }
    // Satellite imagery must never be colour-inverted by the dark theme, and
    // neither must a vector basemap that is already dark.
    el.classList.toggle('fx-sat', which === 'satellite');
  };
  showBase('street');

  // A vector basemap carries its own light/dark style, so the theme is a style
  // swap rather than the old invert() filter. Watching the attribute keeps this
  // inside mapKit: every page that draws a map gets it without asking, and none
  // of them re-create their map when the theme changes.
  //
  // The style is swapped in place. Rebuilding the layer would throw away a
  // WebGL context and set up another one on every toggle, which flashes and
  // costs far more than it saves; the layer is only rebuilt if MapLibre has not
  // finished creating its map yet.
  const themeWatch = vector ? new MutationObserver(() => {
    const gl = (street as Leaflet.MaplibreGLLayer).getMaplibreMap?.();
    if (gl) { gl.setStyle(styleFor(el)); return; }
    const next = makeStreet();
    const live = map.hasLayer(street);
    if (live) map.removeLayer(street);
    street = next;
    if (live && base === 'street') street.addTo(map);
  }) : null;
  themeWatch?.observe(themeHost(el), { attributes: true, attributeFilter: ['data-theme'] });
  // Leaflet's own teardown, so callers keep calling map.remove() and nothing leaks.
  map.on('unload', () => themeWatch?.disconnect());

  // Where "recentre" goes; pages update it via setHome() once they know the route.
  let home: Home = { center, zoom, bounds: null };
  map.fleetSetHome = (bounds) => { home = { ...home, bounds }; };
  const goHome = () => {
    if (home.bounds) map.flyToBounds(home.bounds, { padding: [45, 45], duration: 0.9 });
    else map.flyTo(home.center, home.zoom, { duration: 0.9 });
  };

  // ── Map / Satellite segmented switch (top-left) ──
  const BaseSwitch = L.Control.extend({
    options: { position: 'topleft' },
    onAdd() {
      const box = L.DomUtil.create('div', 'fx-mapseg');
      // Selected by name, not by layer reference: the street layer is rebuilt
      // when the theme changes, and a captured reference would point at the
      // layer that was removed.
      const mk = (label: string, which: 'street' | 'satellite', on: boolean): HTMLButtonElement => {
        const b = L.DomUtil.create('button', on ? 'on' : '', box);
        b.type = 'button'; b.textContent = label;
        L.DomEvent.on(b, 'click', (e) => {
          L.DomEvent.stop(e);
          showBase(which);
          box.querySelectorAll('button').forEach((x) => x.classList.toggle('on', x === b));
        });
        return b;
      };
      mk('Map', 'street', true);
      mk('Satellite', 'satellite', false);
      L.DomEvent.disableClickPropagation(box);
      return box;
    },
  });

  // ── Movement pad (bottom-right): arrows + zoom + recentre ──
  const Pad = L.Control.extend({
    options: { position: 'bottomright' },
    onAdd() {
      const box = L.DomUtil.create('div', 'fx-mappad');
      const btn = (cls: string, html: string, title: string, fn: () => void): void => {
        const b = L.DomUtil.create('button', `fx-pb ${cls}`, box);
        b.type = 'button'; b.innerHTML = html; b.title = title; b.setAttribute('aria-label', title);
        L.DomEvent.on(b, 'click', (e) => { L.DomEvent.stop(e); fn(); });
      };
      const step = (): [number, number] => { const s = map.getSize(); return [s.x / 3, s.y / 3]; };
      btn('up', ICON.up, 'Move up', () => map.panBy([0, -step()[1]], ANIM));
      btn('in', ICON.plus, 'Zoom in', () => map.zoomIn(0.5));
      btn('left', ICON.left, 'Move left', () => map.panBy([-step()[0], 0], ANIM));
      btn('home', ICON.home, 'Recentre', goHome);
      btn('right', ICON.right, 'Move right', () => map.panBy([step()[0], 0], ANIM));
      btn('down', ICON.down, 'Move down', () => map.panBy([0, step()[1]], ANIM));
      btn('out', ICON.minus, 'Zoom out', () => map.zoomOut(0.5));
      L.DomEvent.disableClickPropagation(box);
      L.DomEvent.disableScrollPropagation(box);
      return box;
    },
  });

  new BaseSwitch().addTo(map);
  new Pad().addTo(map);
  return map;
}

// Styles for the controls above; injected once by the fleet shell (theme-aware).
export const MAP_CSS = PLANT_CSS + `
.fx-mapseg { display: flex; padding: 3px; border-radius: 10px; background: var(--fx-surface); box-shadow: 0 2px 8px rgba(0,0,0,0.18); }
.fx-mapseg button { border: none; background: transparent; color: var(--fx-text-2); font: 500 13px -apple-system, BlinkMacSystemFont, 'Inter', sans-serif; padding: 7px 14px; border-radius: 7px; cursor: pointer; }
.fx-mapseg button.on { background: var(--fx-accent-soft); color: var(--fx-accent); font-weight: 600; }
.fx-mappad { display: grid; grid-template-columns: repeat(3, 40px); grid-template-rows: repeat(3, 40px); gap: 6px;
  grid-template-areas: ". up in" "left home right" ". down out"; }
.fx-pb { width: 40px; height: 40px; border-radius: 50%; border: none; background: var(--fx-surface); color: var(--fx-text);
  box-shadow: 0 2px 8px rgba(0,0,0,0.22); display: flex; align-items: center; justify-content: center; cursor: pointer; transition: transform .12s, background .12s; }
.fx-pb:hover { background: var(--fx-surface-2); }
.fx-pb:active { transform: scale(0.92); }
.fx-pb.up { grid-area: up } .fx-pb.down { grid-area: down } .fx-pb.left { grid-area: left } .fx-pb.right { grid-area: right }
.fx-pb.in { grid-area: in } .fx-pb.out { grid-area: out } .fx-pb.home { grid-area: home; color: var(--fx-accent); }
.leaflet-control-attribution { font-size: 10px; background: var(--fx-surface) !important; color: var(--fx-text-3) !important; opacity: .85; }
`;
